/**
 * A curated list rather than free text: an arbitrary learner-controlled string
 * reaches the system prompt, and the tutor's quality varies by topic with
 * nothing to warn the learner.
 *
 * The values are what `startSession` sends and the tutor's prompt reads, so
 * they stay English whatever the interface language (spec §3.1); the screen
 * shows `topicLabel`'s catalog label instead.
 *
 * Two lists, one per register (first-impressions design §6): a child at
 * Pre-A1 was offered "Work" and "Presentations". The kid values are nouns a
 * young child knows and a one-word answer can address, because the prompt
 * reads them as "Keep the conversation around {topic}".
 */
export const ADULT_TOPICS = ['Food', 'Travel', 'Work', 'Family', 'Weekend plans', 'Presentations'] as const;
export const KID_TOPICS = [
  'Animals',
  'Space',
  'Food',
  'Games',
  'Cartoons',
  'Sports',
  'Music',
  'Toys and games',
  'School',
  'Colours',
] as const;

/**
 * The kid games corner (kid play design §3, §7.2): topic values like the
 * rest, so the prompt reads them only through the sanitised CONVERSATION
 * TOPIC section, but they are instructions rather than nouns and so their own
 * list. `tutor_core::play::GAMES` matches them byte for byte, and
 * `//client:scrapbook_contract_test` holds the two lists to one.
 */
export const KID_GAMES = [
  'a riddle game: take turns telling very easy riddles, and cheer every guess',
  'a guessing game: give one easy clue at a time about an animal and let them guess it',
  'a game where they teach you about something they know and you are curious',
  // Kid engagement PRD, F1 and F4: the values of `tutor_core::play::GAMES`
  // exactly, which the server matches. No quote marks in this comment:
  // `tools/scrapbook_contract.mjs` reads this list with a quote pattern.
  'a story game: tell a very short gentle story starring them, and let them choose what happens next',
  'an I spy game: name the colour of something in their room and let them guess what it is, then swap',
  'a would-you-rather game: ask a fun, gentle choice between two things, then let them ask you one',
  'a rhyme game: say an easy word and take turns saying words that rhyme with it, and cheer every try',
] as const;

/**
 * The adult role-play scenes (adult role-play design §3): instructions like
 * the kid games, matched byte for byte by `tutor_core::scene::SCENES`, and
 * held to it by `//client:scrapbook_contract_test`. No quote marks: the
 * contract reads this list with a quote pattern.
 */
export const ADULT_SCENES = [
  'a role-play at a cafe: you are the barista and they are a customer ordering a drink and something to eat',
  'a role-play at a doctor: you are the doctor and they are a patient describing what is wrong',
  'a role-play job interview: you are the interviewer and they are the candidate for a job they choose',
  'a role-play at a hotel desk: you are the receptionist and they are a guest checking in',
  'a role-play in a shop: you are the shop assistant and they are returning something that does not work',
  'a role-play in the street: you are a local and they are a visitor asking the way somewhere',
] as const;

export type KidTopic = (typeof KID_TOPICS)[number];

export type Topic =
  (typeof ADULT_TOPICS)[number] | KidTopic | (typeof KID_GAMES)[number] | (typeof ADULT_SCENES)[number];
